import type { LucideIcon } from 'lucide-react'
import type { ActivityKind, CustomerStatus, RangeKey } from '@/types'

import { Activity, CreditCard, LayoutDashboard, Users } from 'lucide-react'

export interface NavItem {
    href: string
    label: string
    icon: LucideIcon
}

export const NAV_ITEMS: NavItem[] = [
    { href: '/', label: 'Overview', icon: LayoutDashboard },
    { href: '/customers', label: 'Customers', icon: Users },
    { href: '/payments', label: 'Payments', icon: CreditCard },
    { href: '/activity', label: 'Activity', icon: Activity }
]

export interface RangeOption {
    key: RangeKey
    label: string
    heading: string
    comparison: string
    pointComparison: string
    bucket: string
    maxTicks: number
}

export const RANGE_OPTIONS: RangeOption[] = [
    {
        key: '30d',
        label: '30 days',
        heading: 'Last 30 days',
        comparison: 'vs previous 30 days',
        pointComparison: 'vs 30 days ago',
        bucket: 'day',
        maxTicks: 6
    },
    {
        key: '12w',
        label: '12 weeks',
        heading: 'Last 12 weeks',
        comparison: 'vs previous 12 weeks',
        pointComparison: 'vs 12 weeks ago',
        bucket: 'week',
        maxTicks: 6
    },
    {
        key: '12m',
        label: '12 months',
        heading: 'Last 12 months',
        comparison: 'vs previous 12 months',
        pointComparison: 'vs 12 months ago',
        bucket: 'month',
        maxTicks: 12
    }
]

export const DEFAULT_RANGE: RangeKey = '12m'

export const isRangeKey = (value: unknown): value is RangeKey => {
    return RANGE_OPTIONS.some(option => option.key === value)
}

export const getRangeOption = (key: RangeKey): RangeOption => {
    return RANGE_OPTIONS.find(option => option.key === key) ?? RANGE_OPTIONS[RANGE_OPTIONS.length - 1]
}

export const PAGE_SIZE = 20

export type CustomerStatusFilter = 'all' | CustomerStatus

export const CUSTOMER_STATUS_FILTERS: { key: CustomerStatusFilter, label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'active', label: 'Active' },
    { key: 'past_due', label: 'Past due' },
    { key: 'churned', label: 'Churned' }
]

export type CustomerSort = 'newest' | 'oldest' | 'mrr' | 'name'

export const CUSTOMER_SORTS: { key: CustomerSort, label: string }[] = [
    { key: 'newest', label: 'Newest first' },
    { key: 'oldest', label: 'Oldest first' },
    { key: 'mrr', label: 'Highest monthly value' },
    { key: 'name', label: 'Name, A to Z' }
]

export type PaymentStatusFilter = 'all' | 'paid' | 'failed' | 'refunded'

export const PAYMENT_STATUS_FILTERS: { key: PaymentStatusFilter, label: string }[] = [
    { key: 'all', label: 'All' },
    { key: 'paid', label: 'Paid' },
    { key: 'failed', label: 'Failed' },
    { key: 'refunded', label: 'Refunded' }
]

export type ActivityFilter = 'all' | 'signups' | 'plan-changes' | 'cancellations' | 'payment-issues'

export const ACTIVITY_FILTERS: { key: ActivityFilter, label: string, kinds: ActivityKind[] | null }[] = [
    { key: 'all', label: 'All activity', kinds: null },
    { key: 'signups', label: 'New customers', kinds: ['signup'] },
    { key: 'plan-changes', label: 'Plan changes', kinds: ['upgrade', 'downgrade', 'plan_change'] },
    { key: 'cancellations', label: 'Cancellations', kinds: ['churn'] },
    { key: 'payment-issues', label: 'Payment issues', kinds: ['payment_failed', 'refund'] }
]

export const COUNTRIES = [
    'Australia',
    'Austria',
    'Belgium',
    'Brazil',
    'Canada',
    'Denmark',
    'Finland',
    'France',
    'Germany',
    'India',
    'Ireland',
    'Italy',
    'Japan',
    'Mexico',
    'Netherlands',
    'New Zealand',
    'Norway',
    'Poland',
    'Portugal',
    'Singapore',
    'South Africa',
    'Spain',
    'Sweden',
    'Switzerland',
    'United Arab Emirates',
    'United Kingdom',
    'United States'
]

export const DEFAULT_COUNTRY = 'United States'
